import * as XLSX from "xlsx";
import { ConversationDetails, ConversationAnalysisResult } from "@/types/conversation/conversation";

interface ConversationWithAnalysis extends ConversationDetails {
  genie_name?: string;
  analysis_result?: ConversationAnalysisResult;
}

export class ConversationExcelExport {
  static generateExcelWorkbook(conversations: ConversationWithAnalysis[]): XLSX.WorkBook {
    const workbook = XLSX.utils.book_new();

    // Sheet 1: Overview
    const overviewSheet = this.createOverviewSheet(conversations);
    XLSX.utils.book_append_sheet(workbook, overviewSheet, "Overview");

    // Sheet 2: Lead Info (Dynamic Columns)
    const leadInfoSheet = this.createLeadInfoSheet(conversations);
    XLSX.utils.book_append_sheet(workbook, leadInfoSheet, "Extracted Insights");

    // Sheet 3: Key Insights
    const insightsSheet = this.createInsightsSheet(conversations);
    XLSX.utils.book_append_sheet(workbook, insightsSheet, "Key Insights");

    // Sheet 4: Recommendations
    const recommendationsSheet = this.createRecommendationsSheet(conversations);
    XLSX.utils.book_append_sheet(workbook, recommendationsSheet, "Recommendations");

    return workbook;
  }

  private static createOverviewSheet(conversations: ConversationWithAnalysis[]): XLSX.WorkSheet {
    const headers = [
      "Conversation ID",
      "Genie Name",
      "Date",
      "Time",
      "Duration (seconds)",
      "Duration (formatted)",
      "Message Count",
      "Has Analysis",
      "Overall Sentiment",
      "Sentiment Score",
      "Satisfaction Score",
      "Agent Talk %",
      "Customer Talk %",
      "Silence %",
      "Summary",
      "Topics",
      "Goals",
    ];

    const rows = conversations.map((conv) => {
      const analysisResult = conv.analysis_result || {};
      const sentiment = analysisResult.sentiment_analysis;
      const talkMetrics = analysisResult.talk_metrics;
      const date = conv.conversation_date ? new Date(conv.conversation_date) : null;

      return [
        conv.elevenlabs_conversation_id || "",
        conv.genie_name || "",
        date ? date.toLocaleDateString() : "",
        date ? date.toLocaleTimeString() : "",
        conv.duration_seconds || 0,
        this.formatDuration(conv.duration_seconds),
        Array.isArray(conv.transcript) ? conv.transcript.length : 0,
        this.hasAnalysis(conv) ? "Yes" : "No",
        sentiment?.overall_sentiment_type || "",
        sentiment?.overall_sentiment_score != null ? `${Math.round(sentiment.overall_sentiment_score)}%` : "",
        sentiment?.customer_satisfaction_score != null ? `${Math.round(sentiment.customer_satisfaction_score)}%` : "",
        talkMetrics?.agent_talk_percentage != null ? `${Math.round(talkMetrics.agent_talk_percentage)}%` : "",
        talkMetrics?.customer_talk_percentage != null ? `${Math.round(talkMetrics.customer_talk_percentage)}%` : "",
        talkMetrics?.silence_percentage != null ? `${Math.round(talkMetrics.silence_percentage)}%` : "",
        conv.summary || "",
        conv.topics?.join(", ") || "",
        conv.goals?.join(", ") || "",
      ];
    });

    const data = [headers, ...rows];
    const sheet = XLSX.utils.aoa_to_sheet(data);

    // Set column widths
    sheet["!cols"] = [
      { wch: 20 }, // Conversation ID
      { wch: 20 }, // Genie Name
      { wch: 12 }, // Date
      { wch: 10 }, // Time
      { wch: 15 }, // Duration (seconds)
      { wch: 18 }, // Duration (formatted)
      { wch: 14 }, // Message Count
      { wch: 12 }, // Has Analysis
      { wch: 16 }, // Overall Sentiment
      { wch: 14 }, // Sentiment Score
      { wch: 16 }, // Satisfaction Score
      { wch: 12 }, // Agent Talk %
      { wch: 14 }, // Customer Talk %
      { wch: 10 }, // Silence %
      { wch: 50 }, // Summary
      { wch: 30 }, // Topics
      { wch: 30 }, // Goals
    ];

    return sheet;
  }

  private static createLeadInfoSheet(conversations: ConversationWithAnalysis[]): XLSX.WorkSheet {
    // Collect all unique lead info keys across all conversations
    const allKeys = new Set<string>();
    conversations.forEach((conv) => {
      if (conv.lead_info && typeof conv.lead_info === "object") {
        Object.keys(conv.lead_info).forEach((key) => allKeys.add(key));
      }
    });

    const dynamicKeys = Array.from(allKeys).sort();

    // Create headers: fixed columns + dynamic lead info columns
    const headers = ["Conversation ID", "Genie Name", "Date", ...dynamicKeys.map((key) => this.formatColumnHeader(key))];

    const rows = conversations.map((conv) => {
      const date = conv.conversation_date ? new Date(conv.conversation_date) : null;
      const leadInfo = conv.lead_info || {};

      return [
        conv.elevenlabs_conversation_id || "",
        conv.genie_name || "",
        date ? date.toLocaleDateString() : "",
        ...dynamicKeys.map((key) => {
          const value = leadInfo[key];
          if (value === null || value === undefined || value === "") {
            return "";
          }
          return String(value);
        }),
      ];
    });

    const data = [headers, ...rows];
    const sheet = XLSX.utils.aoa_to_sheet(data);

    // Set column widths
    const cols: XLSX.ColInfo[] = [
      { wch: 20 }, // Conversation ID
      { wch: 20 }, // Genie Name
      { wch: 12 }, // Date
    ];
    dynamicKeys.forEach(() => cols.push({ wch: 25 }));
    sheet["!cols"] = cols;

    return sheet;
  }

  private static createInsightsSheet(conversations: ConversationWithAnalysis[]): XLSX.WorkSheet {
    const headers = ["Conversation ID", "Genie Name", "Date", "Insight #", "Insight"];

    const rows: (string | number)[][] = [];
    conversations.forEach((conv) => {
      const analysisResult = conv.analysis_result || {};
      const keyInsights = analysisResult.key_insights || [];
      const date = conv.conversation_date ? new Date(conv.conversation_date) : null;

      keyInsights.forEach((insight, index) => {
        rows.push([
          conv.elevenlabs_conversation_id || "",
          conv.genie_name || "",
          date ? date.toLocaleDateString() : "",
          index + 1,
          insight,
        ]);
      });
    });

    // If no insights, add a placeholder row
    if (rows.length === 0) {
      rows.push(["", "", "", "", "No insights found in the exported conversations"]);
    }

    const data = [headers, ...rows];
    const sheet = XLSX.utils.aoa_to_sheet(data);

    sheet["!cols"] = [
      { wch: 20 }, // Conversation ID
      { wch: 20 }, // Genie Name
      { wch: 12 }, // Date
      { wch: 10 }, // Insight #
      { wch: 80 }, // Insight
    ];

    return sheet;
  }

  private static createRecommendationsSheet(conversations: ConversationWithAnalysis[]): XLSX.WorkSheet {
    const headers = ["Conversation ID", "Genie Name", "Date", "Recommendation #", "Recommendation"];

    const rows: (string | number)[][] = [];
    conversations.forEach((conv) => {
      const analysisResult = conv.analysis_result || {};
      const recommendations = analysisResult.recommendations || [];
      const date = conv.conversation_date ? new Date(conv.conversation_date) : null;

      recommendations.forEach((rec, index) => {
        rows.push([
          conv.elevenlabs_conversation_id || "",
          conv.genie_name || "",
          date ? date.toLocaleDateString() : "",
          index + 1,
          rec,
        ]);
      });
    });

    // If no recommendations, add a placeholder row
    if (rows.length === 0) {
      rows.push(["", "", "", "", "No recommendations found in the exported conversations"]);
    }

    const data = [headers, ...rows];
    const sheet = XLSX.utils.aoa_to_sheet(data);

    sheet["!cols"] = [
      { wch: 20 }, // Conversation ID
      { wch: 20 }, // Genie Name
      { wch: 12 }, // Date
      { wch: 18 }, // Recommendation #
      { wch: 80 }, // Recommendation
    ];

    return sheet;
  }

  private static formatColumnHeader(key: string): string {
    return key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  }

  private static formatDuration(seconds?: number | null): string {
    if (!seconds) return "";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  }

  private static hasAnalysis(conv: ConversationWithAnalysis): boolean {
    return !!(
      conv.summary ||
      conv.goals?.length ||
      conv.topics?.length ||
      (conv.lead_info && Object.keys(conv.lead_info).length)
    );
  }

  static downloadWorkbook(workbook: XLSX.WorkBook, genieName: string): void {
    const fileName = `conversation-insights-${genieName.toLowerCase().replace(/\s+/g, "-")}-${
      new Date().toISOString().split("T")[0]
    }.xlsx`;

    XLSX.writeFile(workbook, fileName);
  }
}
